import React from "react";
import styles from "./UiLibraryComponent.module.css";
import { DropdownMedia } from "@model/UIComponents.model";
import { useDropdownMedia } from "../../../Logic/Functions/Hooks/useDropdownMedia";
import Chevron from "../../../Utils/Assets/SVGs/Chevron";
import Checkmark from "../../../Utils/Assets/SVGs/Checkmark";
import useImageUploadRules from "../../../Logic/Functions/Hooks/useImageUploadRules";

const DropdownWithMedia: React.FC<DropdownMedia> = ({
  value,
  onChange,
  options,
  placeholder = "Select…",
  disabled = false,
  error,
  buildImageSrc,
  showThumbnail = true,
}) => {
  const {
    open,
    activeIdx,
    selected,
    btnRef,
    listRef,
    setOpen,
    setActiveIdx,
    commit,
    onKeyDown,
  } = useDropdownMedia({ value, onChange, options });

  const { buildImageSrc: buildFromHook } = useImageUploadRules({
    maxFiles: 1,
    maxSizeMB: 1,
    requireAtLeastOne: false,
    enforceTotalSize: false,
  });

  const resolveSrc = buildImageSrc ?? buildFromHook;
  const selectedSrc =
    showThumbnail && selected?.imageUrl ? resolveSrc(selected.imageUrl) : null;

  return (
    <div className={styles["dp-wrap"]} data-testid="dropdown-media">
      <button
        ref={btnRef}
        type="button"
        className={[
          styles["dp-trigger"],
          disabled ? styles["dp-disabled"] : "",
          error ? styles["dp-error"] : "",
          !showThumbnail ? styles["dp-noThumb"] : "",
        ].join(" ")}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={onKeyDown}
        disabled={disabled}
        data-testid="dropdown-media-trigger"
      >
        {showThumbnail ? (
          selectedSrc ? (
            <img
              src={selectedSrc}
              alt=""
              className={styles["dp-thumb"]}
              loading="lazy"
              decoding="async"
            />
          ) : (
            <div className={styles["dp-thumbFallback"]} />
          )
        ) : null}

        <div className={styles["dp-triggerText"]}>
          <div className={styles["dp-triggerTitle"]}>
            {selected ? selected.label : placeholder}
          </div>
          {selected?.description ? (
            <div
              className={styles["dp-triggerSub"]}
              title={selected.description || ""}
            >
              {selected.description}
            </div>
          ) : null}
        </div>

        <Chevron className={styles["dp-chev"]} aria-hidden="true" />
      </button>

      {open && (
        <ul
          ref={listRef}
          role="listbox"
          tabIndex={-1}
          className={[
            styles["dp-menu"],
            !showThumbnail ? styles["dp-menuNoThumb"] : "",
          ].join(" ")}
          onKeyDown={onKeyDown}
          data-testid="dropdown-media-menu"
        >
          {options.map((o, idx) => {
            const active = idx === activeIdx;
            const isSelected = o.value === value;
            const src =
              showThumbnail && o.imageUrl ? resolveSrc(o.imageUrl) : null;
            return (
              <li
                key={o.value}
                role="option"
                aria-selected={isSelected}
                className={[
                  styles["dp-item"],
                  active ? styles["dp-itemActive"] : "",
                  isSelected ? styles["dp-itemSelected"] : "",
                  !showThumbnail ? styles["dp-itemNoThumb"] : "", // ✅ NEW
                ].join(" ")}
                onMouseEnter={() => setActiveIdx(idx)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  commit(idx);
                }}
                data-testid={`dropdown-media-item-${idx}`}
              >
                {showThumbnail ? (
                  src ? (
                    <img
                      src={src}
                      alt=""
                      className={styles["dp-itemThumb"]}
                      loading="lazy"
                      decoding="async"
                    />
                  ) : (
                    <div className={styles["dp-itemThumbFallback"]} />
                  )
                ) : null}

                <div className={styles["dp-itemText"]}>
                  <div className={styles["dp-itemTitle"]}>{o.label}</div>
                  {o.description ? (
                    <div
                      className={styles["dp-itemSub"]}
                      title={o.description || ""}
                    >
                      {o.description}
                    </div>
                  ) : null}
                </div>

                {isSelected && (
                  <Checkmark
                    className={styles["dp-check"]}
                    aria-hidden="true"
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}

      {error && (
        <div
          className={styles["dp-errorMsg"]}
          data-testid="dropdown-media-error"
        >
          {error}
        </div>
      )}
    </div>
  );
};

export default DropdownWithMedia;
